import { buildInFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import type { DailyHistorySortKey } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.types';
import type { QueryFilter, QuerySort, TestRunStatus } from '@flowqa/shared';

type BuildTestHistoryQueryStateKeyInput = {
  panelDateQuickFilter: string;
  panelDateWindowEndDay: string | null;
  panelDateWindowStartDay: string | null;
  panelSearchValue: string;
  panelFilterStatuses: TestRunStatus[];
  panelFilterSuites: string[];
  panelFilterTags: string[];
  panelFilterTests: string[];
  historySortDirection: 'asc' | 'desc';
  historySortKey: DailyHistorySortKey;
  historyPage: number;
  historyPageSize: number;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
};

type BuildTestHistoryQueryInputArgs = {
  panelDateWindowEndDay: string | null;
  panelDateWindowStartDay: string | null;
  panelSearchValue: string;
  panelFilterStatuses: TestRunStatus[];
  panelFilterSuites: string[];
  panelFilterTags: string[];
  panelFilterTests: string[];
  historySortDirection: 'asc' | 'desc';
  historySortKey: DailyHistorySortKey;
  historyPage: number;
  historyPageSize: number;
};

type TestHistoryQueryInput = {
  page: number;
  pageSize: number;
  sorts: QuerySort[];
  filters: QueryFilter[];
  search?: string;
  metricsStartDay?: string;
  metricsEndDay?: string;
  include: {
    suites: {
      primary: true;
    };
    dailyHistory: true;
  };
};

export const buildTestHistoryQueryStateKey = (input: BuildTestHistoryQueryStateKeyInput): string =>
  JSON.stringify({
    panelDateQuickFilter: input.panelDateQuickFilter,
    panelSearchValue: input.panelSearchValue,
    panelFilterStatuses: input.panelFilterStatuses,
    panelFilterSuites: input.panelFilterSuites,
    panelFilterTags: input.panelFilterTags,
    panelFilterTests: input.panelFilterTests,
    historySortDirection: input.historySortDirection,
    historySortKey: input.historySortKey,
    panelDateWindowStartDay: input.panelDateWindowStartDay,
    panelDateWindowEndDay: input.panelDateWindowEndDay,
    historyPage: input.historyPage,
    historyPageSize: input.historyPageSize,
    selectedOrganisationId: input.selectedOrganisationId,
    selectedProjectId: input.selectedProjectId,
  });

const buildTestHistoryFilters = (
  panelFilterStatuses: TestRunStatus[],
  panelFilterTests: string[],
  panelFilterSuites: string[],
  panelFilterTags: string[],
): QueryFilter[] =>
  mergeFilters(
    buildInFilter('executionStatus', panelFilterStatuses),
    buildInFilter('id', panelFilterTests),
    buildInFilter('executionSuiteId', panelFilterSuites),
    buildInFilter('tags', panelFilterTags),
  );

export const buildTestHistoryQueryInput = (
  args: BuildTestHistoryQueryInputArgs,
): TestHistoryQueryInput => ({
  page: args.historyPage + 1,
  pageSize: args.historyPageSize,
  search: args.panelSearchValue.trim(),
  sorts: [
    {
      field: args.historySortKey === 'prefixLabel' ? 'suiteName' : args.historySortKey,
      direction: args.historySortDirection,
    },
  ],
  filters: buildTestHistoryFilters(
    args.panelFilterStatuses,
    args.panelFilterTests,
    args.panelFilterSuites,
    args.panelFilterTags,
  ),
  metricsStartDay: args.panelDateWindowStartDay ?? undefined,
  metricsEndDay: args.panelDateWindowEndDay ?? undefined,
  include: {
    suites: {
      primary: true,
    },
    dailyHistory: true,
  },
});
